import { api } from "../api/Middleware";
import {
  AI_AGENTS_URL,
  AI_AGENTS_IMAGES_URL,
  AI_AGENTS_DRAFTS_URL,
  AI_AGENTS_GENERATE_PROMPT_URL,
  AI_AGENTS_COPY_PROMPT_URL,
  AI_AGENTS_EXPORT_URL,
  AI_AGENTS_ACTIVATE_URL,
  AI_AGENTS_VERSIONS_URL,
  AI_AGENTS_RESTORE_VERSION_URL,
  AI_AGENTS_IMAGES_BULK_UPLOAD_URL,
  AI_AGENTS_DRAFTS_CURRENT_URL,
  AI_AGENTS_DRAFTS_SAVE_URL,
  AI_AGENTS_DRAFTS_CLEAR_URL,
  AI_AGENTS_DRAFTS_CLEAR_EXPIRED_URL,
  AI_AGENTS_VERSION_DETAIL_URL,
  AI_AGENTS_CREATE_VERSION_URL,
  AI_AGENTS_EXPORT_VERSION_URL,
  AI_AGENTS_TEST_URL,
  AI_AGENTS_TRAINING_FILES_URL,
  AI_AGENTS_TRAINING_FILES_BULK_UPLOAD_URL,
  AI_AGENTS_PROMPT_CACHE_URL,
  RAG_PROCESS_AGENT_KNOWLEDGE_URL,
  RAG_PROCESS_SINGLE_FILE_URL,
  RAG_CLEAR_AGENT_URL,
} from "../api/Urls";

// =============================
// AI AGENTS SERVICES
// =============================

// Listar todos os agentes
export const listAgents = async () => {
  return await api.get(AI_AGENTS_URL);
};

// Listar agentes básicos (novo endpoint)
export const listBasicAgents = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    // Adicionar parâmetros opcionais
    if (params.search) {
      queryParams.append('search', params.search);
    }
    if (params.limit) {
      queryParams.append('limit', params.limit.toString());
    }
    
    const url = queryParams.toString() 
      ? `${AI_AGENTS_URL}basic_list/?${queryParams.toString()}`
      : `${AI_AGENTS_URL}basic_list/`;
    
    const response = await api.get(url);
    
    return {
      success: true,
      data: response.data,
      agents: response.data.agents || [],
      count: response.data.count || 0,
      account_id: response.data.account_id,
      search_applied: response.data.search_applied || false,
      search_term: response.data.search_term || null
    };
  } catch (error) {
    console.error('Erro ao buscar agentes básicos:', error);
    
    // Tratamento de erros específicos baseado na documentação
    let errorMessage = "Erro desconhecido ao buscar agentes";
    let errorCode = "UNKNOWN_ERROR";
    
    if (error.response?.data) {
      const errorData = error.response.data;
      errorMessage = errorData.error || errorMessage;
      errorCode = errorData.error_code || errorCode;
      
      // Tratar erro específico BASIC_LIST_ERROR
      if (errorCode === "BASIC_LIST_ERROR") {
        errorMessage = "Falha na conexão com o banco de dados. Tente novamente em alguns minutos.";
      }
    } else if (error.request) {
      errorMessage = "Não foi possível conectar ao servidor. Verifique sua conexão de internet.";
      errorCode = "CONNECTION_ERROR";
    } else {
      errorMessage = error.message || errorMessage;
      errorCode = "LOCAL_ERROR";
    }
    
    return {
      success: false,
      error: errorMessage,
      errorCode: errorCode,
      agents: [],
      count: 0,
      account_id: null,
      search_applied: false,
      search_term: params.search || null
    };
  }
};

// Buscar agente específico
export const getAgent = async (id) => {
  return await api.get(AI_AGENTS_URL + `${id}/`);
};

// Buscar detalhes do dashboard do agente
export const getAgentDetails = async (id) => {
  try {
    const response = await api.get(AI_AGENTS_URL + `${id}/agent_dashboard/`);
    
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Erro ao buscar detalhes do agente:', error);
    
    let errorMessage = "Erro desconhecido ao buscar detalhes do agente";
    let errorCode = "UNKNOWN_ERROR";
    
    if (error.response?.data) {
      const errorData = error.response.data;
      errorMessage = errorData.error || errorData.message || errorMessage;
      errorCode = errorData.error_code || errorCode;
    } else if (error.request) {
      errorMessage = "Não foi possível conectar ao servidor. Verifique sua conexão de internet.";
      errorCode = "CONNECTION_ERROR";
    } else {
      errorMessage = error.message || errorMessage;
      errorCode = "LOCAL_ERROR";
    }
    
    return {
      success: false,
      error: errorMessage,
      errorCode: errorCode,
      data: null
    };
  }
};

// Criar novo agente
export const createAgent = async (data) => {
  // Detectar se é FormData (contém arquivos) ou dados JSON
  const isFormData = data instanceof FormData;
  
  const config = isFormData ? {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  } : {};

  return await api.post(AI_AGENTS_URL, data, config);
};

// Atualizar agente existente
export const updateAgent = async (id, data) => {
  // Detectar se é FormData (contém arquivos) ou dados JSON
  const isFormData = data instanceof FormData;
  
  const config = isFormData ? {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  } : {};

  return await api.put(AI_AGENTS_URL + `${id}/`, data, config);
};

// Atualizar parcialmente agente
export const patchAgent = async (id, data) => {
  return await api.patch(AI_AGENTS_URL + `${id}/`, data);
};

// Deletar agente
export const deleteAgent = async (id) => {
  return await api.delete(AI_AGENTS_URL + `${id}/`);
};

// Duplicar agente
export const duplicateAgent = async (id) => {
  return await api.post(AI_AGENTS_URL + `${id}/duplicate/`);
};

// Gerar prompt do agente
export const generateAgentPrompt = async (id) => {
  return await api.post(AI_AGENTS_GENERATE_PROMPT_URL(id));
};

// Log de cópia do prompt
export const copyAgentPrompt = async (id) => {
  return await api.post(AI_AGENTS_COPY_PROMPT_URL(id));
};

// Obter prompt do agente do cache
export const getAgentPrompt = async (agentId) => {
  try {
    if (!agentId) {
      throw new Error("agent_id é obrigatório");
    }

    const response = await api.get(AI_AGENTS_PROMPT_CACHE_URL, {
      params: {
        agent_id: agentId
      }
    });

    return {
      success: true,
      data: response.data,
      prompt: response.data.prompt,
      source: response.data.source,
      cacheKey: response.data.cache_key,
      mode: response.data.mode
    };
  } catch (error) {
    console.error('Erro ao obter prompt do agente:', error);

    let errorMessage = "Erro desconhecido ao obter prompt do agente";
    let errorCode = "UNKNOWN_ERROR";

    if (error.response?.data) {
      const errorData = error.response.data;
      errorMessage = errorData.error || errorData.message || errorMessage;
      errorCode = errorData.error_code || errorCode;
    } else if (error.request) {
      errorMessage = "Não foi possível conectar ao servidor. Verifique sua conexão de internet.";
      errorCode = "CONNECTION_ERROR";
    } else {
      errorMessage = error.message || errorMessage;
      errorCode = "LOCAL_ERROR";
    }

    return {
      success: false,
      error: errorMessage,
      errorCode: errorCode,
      prompt: null,
      source: null,
      cacheKey: null,
      mode: null
    };
  }
};

// Exportar dados do agente
export const exportAgent = async (id) => {
  return await api.get(AI_AGENTS_EXPORT_URL(id));
};

// Ativar/desativar agente
export const activateAgent = async (id) => {
  return await api.post(AI_AGENTS_ACTIVATE_URL(id));
};

// Listar versões do agente
export const getAgentVersions = async (id) => {
  return await api.get(AI_AGENTS_VERSIONS_URL(id));
};

// Restaurar versão específica do agente
export const restoreAgentVersion = async (id, versionNumber) => {
  return await api.post(AI_AGENTS_RESTORE_VERSION_URL(id), {
    version_number: versionNumber,
  });
};

 export const PrepareAgentForTest = async (agentId, setNotify, uid = null, clean_history = null, phone = null) => {
   try {
     // Construir parâmetros de query se fornecidos
     const params = new URLSearchParams();
     if (uid) {
       params.append('uid', uid);
     }
     if (clean_history !== null) {
       params.append('clean_history', clean_history);
     }
     if (phone) {
       params.append('phone', phone);
     }

     const url = params.toString() 
       ? `${AI_AGENTS_URL}${agentId}/test_agent/?${params.toString()}`
       : `${AI_AGENTS_URL}${agentId}/test_agent/`;

     let response = await api.get(url);
     if (response.status === 200) {
       return response.data;
     }
   } catch (error) {
     if (setNotify) {
       setNotify({
         title: "Oops!",
         message:
           error?.response?.data?.error || "Erro ao preparar agente para teste",
         type: "error",
         visible: true,
       });
     }
     throw error.response;
   }
 };

// =============================
// AI AGENTS IMAGES SERVICES
// =============================

// Listar todas as imagens da biblioteca
export const listImages = async (params = {}) => {
  let url = AI_AGENTS_IMAGES_URL;
  
  // Adicionar parâmetros de query se fornecidos
  if (Object.keys(params).length > 0) {
    const queryParams = new URLSearchParams(params);
    url += `?${queryParams.toString()}`;
  }
  
  return await api.get(url);
};

// Buscar imagem específica
export const getImage = async (id) => {
  return await api.get(AI_AGENTS_IMAGES_URL + `${id}/`);
};

// Upload de imagem única
export const uploadImage = async (formData) => {
  return await api.post(AI_AGENTS_IMAGES_URL, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

// Upload múltiplo de imagens
export const bulkUploadImages = async (formData) => {
  return await api.post(AI_AGENTS_IMAGES_BULK_UPLOAD_URL, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

// Atualizar imagem
export const updateImage = async (id, data) => {
  return await api.put(AI_AGENTS_IMAGES_URL + `${id}/`, data);
};

// Atualizar parcialmente imagem
export const patchImage = async (id, data) => {
  return await api.patch(AI_AGENTS_IMAGES_URL + `${id}/`, data);
};

// Deletar imagem
export const deleteImage = async (id) => {
  return await api.delete(AI_AGENTS_IMAGES_URL + `${id}/`);
};

// =============================
// AI AGENTS DRAFTS SERVICES
// =============================

// Listar todos os rascunhos
export const listDrafts = async () => {
  return await api.get(AI_AGENTS_DRAFTS_URL);
};

// Buscar rascunho específico
export const getDraft = async (id) => {
  return await api.get(AI_AGENTS_DRAFTS_URL + `${id}/`);
};

// Buscar rascunho atual (mais recente)
export const getCurrentDraft = async () => {
  return await api.get(AI_AGENTS_DRAFTS_CURRENT_URL);
};

// Criar novo rascunho
export const createDraft = async (data) => {
  return await api.post(AI_AGENTS_DRAFTS_URL, data);
};

// Salvar/atualizar rascunho
export const saveDraft = async (data) => {
  return await api.post(AI_AGENTS_DRAFTS_SAVE_URL, data);
};

// Atualizar rascunho existente
export const updateDraft = async (id, data) => {
  return await api.put(AI_AGENTS_DRAFTS_URL + `${id}/`, data);
};

// Atualizar parcialmente rascunho
export const patchDraft = async (id, data) => {
  return await api.patch(AI_AGENTS_DRAFTS_URL + `${id}/`, data);
};

// Deletar rascunho
export const deleteDraft = async (id) => {
  return await api.delete(AI_AGENTS_DRAFTS_URL + `${id}/`);
};

// Limpar rascunho específico
export const clearDraft = async (id) => {
  return await api.delete(AI_AGENTS_DRAFTS_CLEAR_URL(id));
};

// Limpar rascunhos expirados
export const clearExpiredDrafts = async () => {
  return await api.delete(AI_AGENTS_DRAFTS_CLEAR_EXPIRED_URL);
};

// =============================
// HELPER FUNCTIONS
// =============================

// Função auxiliar para criar FormData com arquivos de imagem
export const createImageFormData = (file, additionalData = {}) => {
  const formData = new FormData();
  formData.append("image", file);
  formData.append("name", file.name);
  formData.append("size", file.size.toString()); // Adicionar campo size obrigatório

  // Adicionar dados adicionais se fornecidos
  Object.keys(additionalData).forEach((key) => {
    formData.append(key, additionalData[key]);
  });

  return formData;
};

// Função auxiliar para criar FormData com múltiplos arquivos
export const createBulkImageFormData = (files, additionalData = {}) => {
  const formData = new FormData();

  // Adicionar cada arquivo
  files.forEach((file) => {
    formData.append("images", file);
  });

  // Adicionar dados adicionais se fornecidos
  Object.keys(additionalData).forEach((key) => {
    formData.append(key, additionalData[key]);
  });

  return formData;
};

// Função auxiliar para preparar dados do agente para envio
export const prepareAgentData = (formData, uploadedImages = []) => {
  return {
    ...formData,
    images: uploadedImages.map((img) => img.id), // Enviar apenas os IDs das imagens
  };
};

// Função auxiliar para criar FormData quando há arquivo de imagem do agente
export const createAgentFormData = (agentData) => {
  const formData = new FormData();
  
  // Verificar se há arquivo de imagem
  const hasImageFile = agentData.agent_image instanceof File;
  
  if (hasImageFile) {
    // Adicionar o arquivo de imagem como binário
    formData.append('agent_image', agentData.agent_image);
  }
  
  // Adicionar todos os outros campos
  Object.keys(agentData).forEach(key => {
    if (key === 'agent_image' && hasImageFile) {
      // Já foi adicionado acima como arquivo binário
      return;
    }
    
    const value = agentData[key];
    
    if (value === null || value === undefined) {
      // Não adicionar campos nulos/undefined
      return;
    }
    
    if (typeof value === 'object') {
      // Serializar objetos/arrays como JSON
      formData.append(key, JSON.stringify(value));
    } else {
      // Adicionar valores primitivos diretamente
      formData.append(key, value);
    }
  });
  
  return formData;
};

// Função auxiliar para determinar se deve usar FormData ou JSON
export const shouldUseFormData = (agentData) => {
  return agentData.agent_image instanceof File;
};

// Função auxiliar para preparar dados do rascunho
export const prepareDraftData = (
  currentStep,
  formData,
  uploadedImages = [],
  agentId = null
) => {
  return {
    current_step: currentStep,
    form_data: formData,
    uploaded_images: uploadedImages.map((img) => img.id),
    agent_id: agentId,
  };
};

// Buscar detalhes de uma versão específica
export const getAgentVersionDetail = async (id, versionNumber) => {
  return await api.get(AI_AGENTS_VERSION_DETAIL_URL(id, versionNumber));
};

// Criar nova versão do agente
export const createAgentVersion = async (id) => {
  return await api.post(AI_AGENTS_CREATE_VERSION_URL(id));
};

// Exportar versão específica
export const exportAgentVersion = async (id, versionNumber = null) => {
  const url = versionNumber
    ? AI_AGENTS_EXPORT_VERSION_URL(id, versionNumber)
    : AI_AGENTS_EXPORT_VERSION_URL(id);
  return await api.get(url);
};

// =============================
// AI AGENTS TRAINING FILES SERVICES (RAG)
// =============================

// Listar todos os arquivos de treinamento
export const listTrainingFiles = async (agentId = null) => {
  const url = agentId 
    ? `${AI_AGENTS_TRAINING_FILES_URL}?agent_id=${agentId}`
    : AI_AGENTS_TRAINING_FILES_URL;
  return await api.get(url);
};

// Buscar arquivo de treinamento específico
export const getTrainingFile = async (id) => {
  return await api.get(AI_AGENTS_TRAINING_FILES_URL + `${id}/`);
};

// Upload de arquivo único de treinamento
export const uploadTrainingFile = async (formData) => {
  return await api.post(AI_AGENTS_TRAINING_FILES_URL, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

// Upload múltiplo de arquivos de treinamento
export const bulkUploadTrainingFiles = async (formData) => {
  return await api.post(AI_AGENTS_TRAINING_FILES_BULK_UPLOAD_URL, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

// Atualizar arquivo de treinamento
export const updateTrainingFile = async (id, data) => {
  return await api.put(AI_AGENTS_TRAINING_FILES_URL + `${id}/`, data);
};

// Atualizar parcialmente arquivo de treinamento
export const patchTrainingFile = async (id, data) => {
  return await api.patch(AI_AGENTS_TRAINING_FILES_URL + `${id}/`, data);
};

// Deletar arquivo de treinamento
export const deleteTrainingFile = async (id) => {
  return await api.delete(AI_AGENTS_TRAINING_FILES_URL + `${id}/`);
};

// Função auxiliar para criar FormData com arquivos de treinamento
export const createTrainingFileFormData = (file, agentId = null, additionalData = {}) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("name", file.name); // Campo esperado pelo TrainingFileCreateSerializer
  
  if (agentId) {
    formData.append("agent_id", agentId);
  }

  // Adicionar dados adicionais se fornecidos
  Object.keys(additionalData).forEach((key) => {
    formData.append(key, additionalData[key]);
  });

  return formData;
};

// Função auxiliar para criar FormData com múltiplos arquivos de treinamento
export const createBulkTrainingFileFormData = (files, agentId = null, additionalData = {}) => {
  const formData = new FormData();

  // Adicionar cada arquivo - BulkTrainingFileUploadSerializer espera apenas 'files'
  files.forEach((file) => {
    formData.append("files", file);
  });

  if (agentId) {
    formData.append("agent_id", agentId);
  }

  // Adicionar dados adicionais se fornecidos
  Object.keys(additionalData).forEach((key) => {
    formData.append(key, additionalData[key]);
  });

  return formData;
};

// =============================
// AI AGENTS TESTING SERVICES (NOVAS FUNÇÕES)
// =============================

// Testar agente com versão específica
export const testAgentMessage = async (payload = {}) => {

  // URL com account_id se fornecido
  try {
    const url = payload.account_id
      ? `${AI_AGENTS_TEST_URL}?account_id=${payload.account_id}`
      : AI_AGENTS_TEST_URL;

    return await api.post(url, payload);
  } catch (error) {
    return error.response.data
  }
};

// =============================
// AI AGENTS VALIDATION & STATS (NOVAS FUNÇÕES)
// =============================

// Validar se agente está pronto para testes
export const validateAgentForTesting = (agent) => {
  const errors = [];
  const warnings = [];

  // Validações obrigatórias
  if (!agent.agent_name?.trim()) {
    errors.push("Nome do agente não definido");
  }

  if (!agent.generated_prompt?.trim()) {
    errors.push("Prompt não foi gerado");
  }

  if (!agent.is_active) {
    warnings.push("Agente está inativo");
  }

  // Validações de funcionalidades
  if (agent.functions_enabled) {
    if (
      !agent.function_schemas ||
      Object.keys(agent.function_schemas).length === 0
    ) {
      warnings.push("Funções habilitadas mas nenhum schema configurado");
    }
  }

  // Validações de fluxos
  if (!agent.default_flow || Object.keys(agent.default_flow).length === 0) {
    warnings.push("Nenhum fluxo padrão configurado");
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

// Gerar estatísticas sobre o agente
export const generateAgentStats = (agent) => {
  const stats = {
    basicInfo: {
      name: agent.agent_name,
      area: agent.agent_area,
      gender: agent.agent_gender,
      version: agent.version,
      isActive: agent.is_active,
    },
    content: {
      promptLength: agent.generated_prompt?.length || 0,
      businessInfoLength: agent.business_info?.length || 0,
      prohibitedWordsCount: agent.prohibited_words
        ? agent.prohibited_words.split(",").length
        : 0,
      preferredWordsCount: agent.preferred_words
        ? agent.preferred_words.split(",").length
        : 0,
    },
    personality: {
      personaTraits: agent.agent_persona?.length || 0,
      language: agent.language?.length || 0,
      languageStyles: agent.language_style?.length || 0,
      fundamentalRules: agent.fundamental_rules?.length || 0,
    },
    flows: {
      defaultFlowSteps: agent.default_flow
        ? Object.keys(agent.default_flow).length
        : 0,
      specialFlowsCount: agent.special_flows?.length || 0,
    },
    functions: {
      enabled: agent.functions_enabled,
      schemasCount: agent.function_schemas
        ? Object.keys(agent.function_schemas).length
        : 0,
      responsesCount: agent.function_responses
        ? Object.keys(agent.function_responses).length
        : 0,
    },
    interactions: {
      enabled: agent.interactions
        ? Object.keys(agent.interactions).filter(
            (k) => agent.interactions[k]?.enabled
          ).length
        : 0,
      total: agent.interactions ? Object.keys(agent.interactions).length : 0,
    },
  };

  return stats;
};

// =============================
// AI AGENTS COMPLETE DATA LOADER (NOVA FUNÇÃO)
// =============================

// Preparar dados completos do agente para testes
export const prepareAgentTestData = async (agentId) => {
  try {
    // Carregar dados do agente
    const agentResponse = await getAgent(agentId);
    if (!agentResponse.data) {
      throw new Error("Agente não encontrado");
    }

    const agent = agentResponse.data;

    // Carregar versões
    let versions = [];
    try {
      const versionsResponse = await getAgentVersions(agentId);
      versions = versionsResponse.data.results || versionsResponse.data || [];
    } catch (error) {
      console.warn(
        "Falha ao carregar versões, usando apenas versão atual:",
        error
      );
    }

    // Preparar versão atual
    const currentVersion = {
      id: "current",
      version_number: agent.version,
      agent_data: agent,
      generated_prompt: agent.generated_prompt,
      created_at: agent.updated_at,
      is_current: true,
    };

    // Combinar versão atual com histórico
    const allVersions = [currentVersion, ...versions];

    return {
      success: true,
      data: {
        agent,
        versions: allVersions,
        currentVersion,
      },
    };
  } catch (error) {
    console.error("Erro ao preparar dados de teste:", error);
    return {
      success: false,
      error:
        error.response?.data?.detail ||
        error.message ||
        "Erro ao carregar dados do agente",
    };
  }
};

// =============================
// DOWNLOAD HELPER (NOVA FUNÇÃO)
// =============================

// Baixar versão exportada como arquivo JSON
export const downloadAgentVersionExport = async (
  agentId,
  versionNumber = null
) => {
  try {
    const response = await exportAgentVersion(agentId, versionNumber);

    if (!response.data) {
      throw new Error("Dados de export não encontrados");
    }

    const data = response.data;
    const filename = `${data.agent_info?.name || "agente"}_v${
      data.agent_info?.version || "1"
    }.json`;

    // Criar blob e download
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    return {
      success: true,
      filename,
    };
  } catch (error) {
    console.error("Erro ao baixar export:", error);
    return {
      success: false,
      error:
        error.response?.data?.detail ||
        error.message ||
        "Erro ao baixar arquivo",
    };
  }
};

// =============================
// KNOWLEDGE BASE SERVICES (BASEADO NA API DRF)
// =============================

// Listar todos os arquivos da base de conhecimento com informações de quota
export const getKnowledgeBaseFiles = async (filters = {}) => {
  const params = new URLSearchParams();
  
  // Adicionar filtros se fornecidos
  if (filters.agent_id) params.append('agent_id', filters.agent_id);
  if (filters.file_type) params.append('file_type', filters.file_type);
  if (filters.is_processed !== undefined) params.append('is_processed', filters.is_processed);
  if (filters.search) params.append('search', filters.search);
  if (filters.ordering) params.append('ordering', filters.ordering);
  if (filters.page) params.append('page', filters.page);
  if (filters.page_size) params.append('page_size', filters.page_size);
  
  const url = params.toString() ? `${AI_AGENTS_TRAINING_FILES_URL}?${params.toString()}` : AI_AGENTS_TRAINING_FILES_URL;
  
  return await api.get(url);
};

// Obter detalhes de um arquivo específico da base de conhecimento
export const getKnowledgeBaseFile = async (fileId) => {
  return await api.get(`${AI_AGENTS_TRAINING_FILES_URL}${fileId}/`);
};

// Upload de arquivo para base de conhecimento
export const uploadKnowledgeBaseFile = async (formData, params = {}) => {
  let url = AI_AGENTS_TRAINING_FILES_URL;
  
  // Adicionar parâmetros de query se fornecidos
  if (Object.keys(params).length > 0) {
    const queryParams = new URLSearchParams(params);
    url += `?${queryParams.toString()}`;
  }
  
  return await api.post(url, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

// Upload em lote de arquivos para base de conhecimento
export const bulkUploadKnowledgeBaseFiles = async (formData, params = {}) => {
  let url = AI_AGENTS_TRAINING_FILES_BULK_UPLOAD_URL;
  
  // Adicionar parâmetros de query se fornecidos
  if (Object.keys(params).length > 0) {
    const queryParams = new URLSearchParams(params);
    url += `?${queryParams.toString()}`;
  }
  
  return await api.post(url, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

// Deletar arquivo da base de conhecimento
export const deleteKnowledgeBaseFile = async (fileId) => {
  return await api.delete(`${AI_AGENTS_TRAINING_FILES_URL}${fileId}/`);
};

// Deletar múltiplos arquivos da base de conhecimento (bulk delete)
export const bulkDeleteKnowledgeBaseFiles = async (fileIds) => {
  return await api.delete(`${AI_AGENTS_TRAINING_FILES_URL}bulk_delete/`, {
    data: {
      ids: fileIds
    }
  });
};

// Função auxiliar para criar FormData para upload de arquivo da base de conhecimento
export const createKnowledgeBaseFileFormData = (file, additionalData = {}) => {
  const formData = new FormData();
  
  // Arquivo obrigatório
  formData.append("file", file);
  
  // Nome personalizado ou usar o nome original
  const fileName = additionalData.name || file.name;
  formData.append("name", fileName);
  
  // Informações adicionais opcionais
  if (additionalData.description) {
    formData.append("description", additionalData.description);
  }
  
  // Agent ID se fornecido
  if (additionalData.agent_id) {
    formData.append("agent_id", additionalData.agent_id.toString());
  }
  
  // Metadados do arquivo
  formData.append("original_filename", file.name);
  formData.append("file_size", file.size.toString());
  formData.append("file_type", file.type);
  
  return formData;
};

// Função auxiliar para criar FormData para upload em lote
export const createBulkKnowledgeBaseFileFormData = (files, additionalData = {}) => {
  const formData = new FormData();
  
  // Adicionar todos os arquivos
  files.forEach((file, index) => {
    formData.append("files", file);
    
    // Metadados por arquivo
    formData.append(`names[${index}]`, file.name);
    formData.append(`original_filenames[${index}]`, file.name);
    formData.append(`file_sizes[${index}]`, file.size.toString());
    formData.append(`file_types[${index}]`, file.type);
  });
  
  // Dados adicionais globais
  if (additionalData.description) {
    formData.append("global_description", additionalData.description);
  }
  
  // Agent ID se fornecido
  if (additionalData.agent_id) {
    formData.append("agent_id", additionalData.agent_id.toString());
  }
  
  return formData;
};

// Validar arquivo antes do upload
export const validateKnowledgeBaseFile = (file, options = {}) => {
  const errors = [];
  const warnings = [];
  
  // Configurações padrão
  const config = {
    maxSize: options.maxSize || 5 * 1024 * 1024, // 5MB
    allowedTypes: options.allowedTypes || [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ],
    maxNameLength: options.maxNameLength || 255,
    ...options
  };
  
  // Validar existência do arquivo
  if (!file) {
    errors.push("Arquivo não fornecido");
    return { isValid: false, errors, warnings };
  }
  
  // Validar tamanho
  if (file.size > config.maxSize) {
    errors.push(`Arquivo muito grande: ${(file.size / (1024 * 1024)).toFixed(2)}MB (máximo: ${(config.maxSize / (1024 * 1024)).toFixed(2)}MB)`);
  }
  
  // Validar tipo
  if (!config.allowedTypes.includes(file.type)) {
    errors.push(`Tipo de arquivo não suportado: ${file.type}`);
  }
  
  // Validar nome
  if (file.name.length > config.maxNameLength) {
    errors.push(`Nome do arquivo muito longo (máximo: ${config.maxNameLength} caracteres)`);
  }
  
  // Validar caracteres especiais no nome
  const invalidChars = /[<>:"/\\|?*]/;
  if (invalidChars.test(file.name)) {
    warnings.push("Nome do arquivo contém caracteres especiais que podem causar problemas");
  }
  
  // Avisos sobre tamanho
  if (file.size > 1024 * 1024) { // > 1MB
    warnings.push(`Arquivo grande (${(file.size / (1024 * 1024)).toFixed(2)}MB) pode demorar para processar`);
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    fileInfo: {
      name: file.name,
      size: file.size,
      type: file.type,
      sizeFormatted: formatFileSize(file.size)
    }
  };
};

// Função auxiliar para formatar tamanho de arquivo
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// =============================
// RAG/KNOWLEDGE PROCESSING SERVICES
// =============================

// Processar base de conhecimento de um agente (executa task assíncrona)
export const processAgentKnowledge = async ({ agent_id, file_ids = null }) => {
  if (!agent_id) {
    throw new Error("agent_id é obrigatório");
  }

  const data = {
    agent_id,
  };

  // Incluir file_ids apenas se fornecido (para processar arquivos específicos)
  if (file_ids && Array.isArray(file_ids) && file_ids.length > 0) {
    data.file_ids = file_ids;
  }

  try {
    const response = await api.post(RAG_PROCESS_AGENT_KNOWLEDGE_URL, data);
    
    // Tratar diferentes status de resposta
    const status = response.status;
    const responseData = response.data;
    
    return {
      ...response,
      data: {
        ...responseData,
        statusInfo: {
          code: status,
          type: status === 200 ? 'success' : 
                status === 206 ? 'partial_success' : 
                status === 400 ? 'processing_failed' : 
                status === 404 ? 'not_found' : 'error',
          message: status === 200 ? 'Processamento completo' :
                   status === 206 ? 'Processamento parcial' :
                   status === 400 ? 'Falha no processamento' :
                   status === 404 ? 'Agente não encontrado' : 'Erro interno'
        }
      }
    };
  } catch (error) {
    // Se o erro tem response data (status 4xx/5xx), incluir na estrutura padrão
    if (error.response?.data) {
      const status = error.response.status;
      const errorData = error.response.data;
      
      throw {
        ...error,
        response: {
          ...error.response,
          data: {
            ...errorData,
            statusInfo: {
              code: status,
              type: status === 400 ? 'processing_failed' : 
                    status === 404 ? 'not_found' : 'error',
              message: status === 400 ? 'Falha no processamento' :
                       status === 404 ? 'Agente não encontrado' : 'Erro interno'
            }
          }
        }
      };
    }
    
    throw error;
  }
};

// Processar um único arquivo de treinamento (executa task assíncrona)
export const processSingleFile = async ({ file_id, agent_id }) => {
  if (!file_id || !agent_id) {
    throw new Error("file_id e agent_id são obrigatórios");
  }

  const data = {
    file_id,
    agent_id,
  };

  return await api.post(RAG_PROCESS_SINGLE_FILE_URL, data);
};

// Limpeza profunda de todos os dados RAG de um agente
export const clearAgentRag = async (agentId) => {
  if (!agentId) {
    throw new Error("agentId é obrigatório");
  }
  return await api.post(RAG_CLEAR_AGENT_URL(agentId));
};

// =============================
// PUBLIC LINK SERVICES
// =============================

// Gerar link público para teste do agente
export const generatePublicLink = async (agentId, phones = []) => {
  if (!agentId) {
    throw new Error("agentId é obrigatório");
  }

  const data = {
    phones: phones
  };

  return await api.post(`${AI_AGENTS_URL}${agentId}/generate_public_link/`, data);
};

// Obter informações do link público do agente
export const getPublicTestInfo = async (agentId) => {
  if (!agentId) {
    throw new Error("agentId é obrigatório");
  }

  return await api.get(`${AI_AGENTS_URL}${agentId}/get_public_test_info/`);
};

// Atualizar telefones autorizados do link público
export const updatePublicTestPhones = async (agentId, phones = []) => {
  if (!agentId) {
    throw new Error("agentId é obrigatório");
  }

  const data = {
    phones: phones
  };

  return await api.patch(`${AI_AGENTS_URL}${agentId}/update_public_test_phones/`, data);
};

// Ativar/desativar link público
export const togglePublicTest = async (agentId, isActive) => {
  if (!agentId) {
    throw new Error("agentId é obrigatório");
  }

  const data = {
    is_active: isActive
  };

  return await api.patch(`${AI_AGENTS_URL}${agentId}/toggle_public_test/`, data);
};

// Deletar link público completamente
export const deletePublicTest = async (agentId) => {
  if (!agentId) {
    throw new Error("agentId é obrigatório");
  }

  return await api.delete(`${AI_AGENTS_URL}${agentId}/delete_public_test/`);
};
